import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { question: "", answer: "", category: "general", order: 0, published: true };

export default function AdminFaq() {
  useSEO("Admin · FAQ");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/faqs").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/admin/faqs/${editId}`, { ...form, order: Number(form.order) || 0 });
      else await api.post("/admin/faqs", { ...form, order: Number(form.order) || 0 });
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/faqs/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <AdminHeader title="FAQ" subtitle="Manage frequently asked questions"
        action={<Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="gradient-brand text-brand-navy font-semibold" data-testid="new-faq-btn"><Plus className="h-4 w-4 mr-1.5" /> Add FAQ</Button>} />
      <div className="space-y-3">
        {rows.length === 0 ? <p className="text-slate-500">No FAQs yet.</p>
          : rows.map((f) => (
            <div key={f.id} className="card-surface rounded-2xl p-5" data-testid={`admin-faq-${f.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div><p className="font-display font-semibold text-white">{f.question}</p><p className="mt-1 text-sm text-slate-400">{f.answer}</p></div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => { setForm({ ...empty, ...f }); setEditId(f.id); setOpen(true); }} className="text-slate-500 hover:text-brand-cyan"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(f.id)} className="text-slate-500 hover:text-red-400" data-testid={`del-faq-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-surface border-white/10">
          <DialogHeader><DialogTitle className="text-white">{editId ? "Edit" : "Add"} FAQ</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label className="text-slate-300">Question</Label><Input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div><Label className="text-slate-300">Answer</Label><Textarea required rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer" className="mt-1.5 bg-white/5 border-white/10" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
            <DialogFooter><Button type="submit" className="gradient-brand text-brand-navy font-semibold" data-testid="faq-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
